import { NavigateFunction } from "react-router-dom";
import React, { Fragment } from "react";
import { observer } from "mobx-react";
import { convertToRaw, EditorState } from "draft-js";
import DisabledIcon from "../../../../../../images/checkout/disable-grey.svg";
import TitleBuilderElementIcon from "../../../../../../images/checkout/title-builder-grey.svg";
import ReshareIcon from "../../../../../../images/checkout/reshare-grey.svg";
import CheckoutIFrame from "./CheckoutIFrame";
import {
  CustomLabel,
  DraftEditor,
  FlexContainer,
  CollapsiblePanel,
  IconButton,
  Input,
  IOption,
  SingleSelect,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CheckOutAfterPayModel, MediaModel } from "../../../shared/models";
import {
  CheckOutRedirectOption,
  CheckOutRedirectTypes,
} from "../../../shared/enums";
import { CheckOutStore } from "../../../shared/store";
import { MediaBoxes, RedirectInputs } from "../../../shared/components";
import {
  AfterPayWrapper,
  CheckoutDetailSection,
  ProductDetailWrapper,
} from "../../../shared/styles";

type Props = {
  checkoutStore: CheckOutStore;
  buttonBgColor: string;
  navigate: NavigateFunction;
};

const iconMap = {
  None: DisabledIcon,
  Message: TitleBuilderElementIcon,
  Redirect: ReshareIcon,
};

const CheckOutAfterPayPage: React.FC<Props> = ({ checkoutStore }) => {
  if (!checkoutStore?.afterPayPage) return null;

  function updateAfterPage(
    key: keyof CheckOutAfterPayModel,
    value: string | boolean | EditorState
  ) {
    const clonedState = new CheckOutAfterPayModel(checkoutStore.afterPayPage);
    switch (key) {
      case "type":
        clonedState.type = value as keyof typeof CheckOutRedirectOption;
        break;
      case "titleEditorState":
        const editorState = value as EditorState;
        clonedState.titleEditorState = editorState;
        clonedState.title = editorState.getCurrentContent().getPlainText();
        clonedState.titleEditorRawContent = JSON.stringify(
          convertToRaw(editorState.getCurrentContent())
        );
        break;
      case "descriptionEditorState":
        const descriptionEditorState = value as EditorState;
        clonedState.descriptionEditorState = descriptionEditorState;
        clonedState.description = descriptionEditorState
          .getCurrentContent()
          .getPlainText();
        clonedState.descriptionEditorRawContent = JSON.stringify(
          convertToRaw(descriptionEditorState.getCurrentContent())
        );
        break;
      case "showButton":
        clonedState.showButton = value as boolean;
        break;
      case "redirectType":
        clonedState.redirectType = value as keyof typeof CheckOutRedirectTypes;
        break;
      case "redirectValue":
        clonedState.redirectValue = value as string;
        break;
      case "buttonText":
        clonedState.buttonText = value as string;
        break;
      case "showReceipt":
        clonedState.showReceipt = value as boolean;
        break;
      case "allowDownload":
        clonedState.allowDownload = value as boolean;
        break;
    }

    checkoutStore.updateAfterPayPage(clonedState);
  }

  function checkBox(
    value: boolean,
    fieldName: keyof CheckOutAfterPayModel,
    label: string
  ) {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => updateAfterPage(fieldName, e.target.checked)}
      />
    );
  }

  function updateMedia(media?: MediaModel) {
    const clonedState = new CheckOutAfterPayModel(checkoutStore.afterPayPage);
    clonedState.media = media;
    checkoutStore.updateAfterPayPage(clonedState);
  }

  function singleSelect(
    selectedKey: string,
    fieldName: keyof CheckOutAfterPayModel,
    options: IOption[],
    hasError: boolean,
    className?: string
  ) {
    return (
      <SingleSelect
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={(option) => {
          updateAfterPage(fieldName, option.value);
        }}
        className={className}
      />
    );
  }

  function redirectOptions() {
    return Object.keys(CheckOutRedirectTypes).map((key) => {
      return {
        label: CheckOutRedirectTypes[key as keyof typeof CheckOutRedirectTypes],
        value: key,
      };
    });
  }

  function renderRedirectByType() {
    switch (checkoutStore.afterPayPage.type) {
      case "None":
        return null;
      case "Message":
        return renderMessage();
      case "Redirect":
        return rendeRedirectInputs();
      default:
        return null;
    }
  }

  function rendeRedirectInputs() {
    return (
      <>
        {singleSelect(
          checkoutStore.afterPayPage.redirectType,
          "redirectType",
          redirectOptions(),
          false,
          "after-pay-select"
        )}
        <RedirectInputs
          type={checkoutStore.afterPayPage.redirectType}
          value={checkoutStore.afterPayPage.redirectValue}
          onChange={(value: string | boolean | EditorState) =>
            updateAfterPage("redirectValue", value)
          }
          hasError={checkoutStore.afterPayPage.hasError("redirectValue")}
          className="after-pay-web-url"
        />
      </>
    );
  }

  function renderMessage() {
    return (
      <FlexContainer fxLayout="column" spacing={20}>
        <div>
          <CustomLabel className="checkout-headers">Media</CustomLabel>
        </div>
        <MediaBoxes
          key={"medias"}
          multiple={false}
          medias={
            (checkoutStore.afterPayPage.media && [
              checkoutStore.afterPayPage.media,
            ]) ||
            []
          }
          onSelectLibraryMedia={(media) => updateMedia(media)}
          onUpload={(medias: MediaModel[]) => updateMedia(medias[0])}
          onDelete={() => {
            updateMedia(undefined);
          }}
        />
        <CustomLabel className="checkout-headers">Title</CustomLabel>
        <DraftEditor
          editorState={checkoutStore.afterPayPage.titleEditorState}
          onChange={(newValue: EditorState) =>
            updateAfterPage("titleEditorState", newValue)
          }
          className="product-detail-editor"
          showErrorBorder={checkoutStore.afterPayPage.hasError(
            "titleEditorState"
          )}
        />

        <CustomLabel className="checkout-headers">Description</CustomLabel>
        <DraftEditor
          editorState={checkoutStore.afterPayPage.descriptionEditorState}
          onChange={(newValue: EditorState) =>
            updateAfterPage("descriptionEditorState", newValue)
          }
          className="product-detail-editor"
        />
        <CustomLabel className="checkout-headers">Button</CustomLabel>

        {checkBox(
          checkoutStore.afterPayPage.showButton,
          "showButton",
          "Show Button"
        )}
        {checkoutStore.afterPayPage.showButton && (
          <>
            {rendeRedirectInputs()}
            <Input
              value={checkoutStore.afterPayPage.buttonText}
              id={`txtButtonText`}
              type="text"
              placeholder="Button Text"
              error={checkoutStore.afterPayPage.hasError("buttonText")}
              onChange={(e) => updateAfterPage("buttonText", e.target.value)}
              className="styled-input afterpay-text-input"
              maxLength={50}
            />
          </>
        )}
        {/* COMMENTED BELOW CODE AS PER JEFF MENTIONED IN FIGMA BECAUSE FUNCTIONALITY NOT ADDED */}
        {/* <CustomLabel className="checkout-headers">Receipt</CustomLabel>

        {checkBox(
          checkoutStore.afterPayPage.showReceipt,
          "showReceipt",
          "Show Receipt"
        )}
        {checkBox(
          checkoutStore.afterPayPage.allowDownload,
          "allowDownload",
          "Allow Download"
        )} */}
      </FlexContainer>
    );
  }

  function getRedirectDescription(key: keyof typeof CheckOutRedirectOption) {
    switch (key) {
      case "None":
        break;
      case "Message":
        return (
          <CustomLabel>Show custom message with purchase receipt.</CustomLabel>
        );
      case "Redirect":
        return <CustomLabel>Redirect to a website or mobile page.</CustomLabel>;
      default:
        return null;
    }
  }

  return (
    <AfterPayWrapper>
      <ProductDetailWrapper margin="0 0 25px">
        <FlexContainer>
          <CheckoutDetailSection>
            <CollapsiblePanel title="Redirect">
              <CustomLabel className="checkout-headers">Type</CustomLabel>
              <FlexContainer spacing={20} margin="20px 0px">
                {Object.keys(CheckOutRedirectOption).map((key) => {
                  const isActive = checkoutStore.afterPayPage.type === key;
                  return (
                    <div key={key}>
                      <IconButton
                        className={
                          isActive
                            ? "active blue-btn-green-hovered"
                            : "blue-btn-green-hovered"
                        }
                        onClick={() => updateAfterPage("type", key)}
                        justifyContent="center"
                      >
                        <img
                          src={
                            iconMap[key as keyof typeof CheckOutRedirectOption]
                          }
                        />
                        {
                          CheckOutRedirectOption[
                            key as keyof typeof CheckOutRedirectOption
                          ]
                        }
                      </IconButton>
                    </div>
                  );
                })}
              </FlexContainer>
              <FlexContainer fxLayout="column" spacing={20}>
                {Object.keys(CheckOutRedirectOption).map((key) => {
                  const isActive = checkoutStore.afterPayPage.type === key;
                  return (
                    isActive &&
                    getRedirectDescription(
                      key as keyof typeof CheckOutRedirectOption
                    )
                  );
                  // return (
                  //     <Fragment key={key}>
                  //         {isActive && getRedirectDescription(key as keyof typeof CheckOutRedirectOption)}
                  //     </Fragment>
                  // );
                })}
                {renderRedirectByType()}
              </FlexContainer>
            </CollapsiblePanel>
          </CheckoutDetailSection>
          {checkoutStore.afterPayPage.type !==
            CheckOutRedirectOption.Redirect && (
            <CheckoutIFrame checkoutStore={checkoutStore} />
          )}
        </FlexContainer>
      </ProductDetailWrapper>
    </AfterPayWrapper>
  );
};

export default withRouter(observer(CheckOutAfterPayPage));
